import { ServicesTableRow } from '@/components/bulkQueue/types';
import {
    IUpdateAppointmentServicesInput,
    useBulkUpdateAppointmentServices,
    useBulkUpdateVisitAppointmentServices,
} from '@/components/visitRequests/mutations/updateAppointmentServices';
import type { MutationHookOptions } from '@apollo/client/react/types/types';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import invariant from 'tiny-invariant';

export interface IClinicianAppointmentSelectedServices {
    serviceIds: string[];
}
export interface IFocusableVisitRequest {
    id: string;
    disciplines: {
        id: string;
    }[];
    appointments: {
        id: string;
        services: {
            id: string;
            name: string;
        }[];
    }[];
    availableServices: {
        id: string;
        name: string;
        isVirtual: boolean;
    }[];
}

type AvailableServicesGetter = (
    row: ServicesTableRow,
) => IFocusableVisitRequest['availableServices'];

const defaultGetAvailableServices: AvailableServicesGetter = (row) => {
    switch (row.__typename) {
        case 'VisitRequest':
            return row.availableServices;
        case 'Visit':
            return row.availableServices;
        default:
            return [];
    }
};

const getAppointments = (row: ServicesTableRow | null): IFocusableVisitRequest['appointments'] => {
    if (!row) {
        return [];
    }
    switch (row.__typename) {
        case 'VisitRequest':
            return row.appointments;
        case 'Visit':
            return row.appointments ?? [];
        default:
            return [];
    }
};

export interface IVisitRequestSelectionState {
    isFocused: boolean;
    isSelected: boolean;
    isCompatibleWithFocusedEditing: boolean;
    setIsSelected: (selected: boolean) => void;
    focus: () => void;
    cancelFocus: () => void;
}

export interface ISelectedAppointmentServices {
    value: IClinicianAppointmentSelectedServices[];
    setValue: (services: IClinicianAppointmentSelectedServices[]) => void;
    toDisplay: {
        services: { id: string; name: string }[];
    }[];
}

type UseBulkAppointmentServicesSelectionOptions = {
    availableServicesGetter?: AvailableServicesGetter;
};

export const useBulkAppointmentServicesSelection = (
    options: UseBulkAppointmentServicesSelectionOptions = {},
) => {
    const { availableServicesGetter } = options;
    const getAvailableServices = useCallback(
        (row: ServicesTableRow | null): IFocusableVisitRequest['availableServices'] => {
            if (!row) {
                return [];
            }
            return availableServicesGetter
                ? availableServicesGetter(row)
                : defaultGetAvailableServices(row);
        },
        [availableServicesGetter],
    );

    // The specific visit request row that is being focused on to select services.
    const [focusedVisitRequest, setFocusedVisitRequestState] = useState<ServicesTableRow | null>(
        null,
    );
    const focusedVisitRequestRef = useRef(focusedVisitRequest);
    focusedVisitRequestRef.current = focusedVisitRequest;
    const focusedDisciplineIds = useMemo(() => {
        return new Set(focusedVisitRequest?.disciplines.map((discipline) => discipline.id) ?? []);
    }, [focusedVisitRequest]);

    // When the focused visit request changes, reset selection.
    const focusedId = focusedVisitRequest?.id;
    useEffect(() => {
        const current = focusedVisitRequestRef.current;
        if (current) {
            setSelectedVisitRequests([current]);
            setSelectedAppointmentServicesState(
                getAppointments(current).map((appointment) => ({
                    serviceIds: appointment.services.map((service) => service.id),
                })),
            );
        } else {
            setSelectedVisitRequests([]);
            setSelectedAppointmentServicesState([]);
        }
    }, [focusedId]);

    // The set of visit request ids that are currently selected -- when editing, it will
    // always include the id of the visit request being edited.
    const [selectedVisitRequests, setSelectedVisitRequests] = useState<ServicesTableRow[]>([]);

    // When editing the focused visit request, this state will store the selected services per appointment.
    // TODO(jessica): When there are more complex rules about available services besides discipline
    // filtering, we will need to filter the selection down to those that are compatible.
    const [selectedAppointmentServicesState, setSelectedAppointmentServicesState] = useState<
        IClinicianAppointmentSelectedServices[]
    >([]);

    // Skeleton "appointments" populated with the selected service values that
    // can be used to preview the end state of selected visit request rows.
    const selectedAppointmentsToDisplay = useMemo(() => {
        const availableServices = getAvailableServices(focusedVisitRequest);
        if (!availableServices.length) {
            return [];
        }
        return selectedAppointmentServicesState.map((appointment) => {
            return {
                services: appointment.serviceIds.map((serviceId) => {
                    const service = availableServices.find((service) => service.id === serviceId);
                    invariant(service, `Service ${serviceId} not found`);
                    return service;
                }),
            };
        });
    }, [focusedVisitRequest, selectedAppointmentServicesState, getAvailableServices]);

    // Focus + Selection Compatibility -- "Can these visits be edited together?"
    const isCompatibleWithFocusedSelection = useCallback(
        (row: ServicesTableRow) => {
            const { id, disciplines } = row;
            const appointments = getAppointments(row);
            const availableServices = getAvailableServices(row);
            // Focused visit request is always compatible with itself.
            if (id === focusedVisitRequest?.id) {
                return true;
            }
            // Other visit reqeusts are compatible if their disciplines are the same...
            const disciplineIds = new Set(disciplines.map((discipline) => discipline.id));
            if (!_.isEqual(disciplineIds, focusedDisciplineIds)) {
                return false;
            }
            // And number of appointments are the same...
            if (appointments.length !== getAppointments(focusedVisitRequest).length) {
                return false;
            }
            // And all selected services are available...
            const availableServiceIds = new Set(availableServices.map((service) => service.id));
            for (const appointment of selectedAppointmentServicesState) {
                if (
                    !appointment.serviceIds.every((serviceId) => availableServiceIds.has(serviceId))
                ) {
                    return false;
                }
            }
            return true;
        },
        [
            focusedVisitRequest,
            focusedDisciplineIds,
            selectedAppointmentServicesState,
            getAvailableServices,
        ],
    );

    // Bulk Mutation
    const { updateAppointmentServices, loading: isUpdatingVisitRequestAppointmentServices } =
        useBulkUpdateAppointmentServices();
    const { updateVisitAppointmentServices, loading: isUpdatingVisitAppointmentServices } =
        useBulkUpdateVisitAppointmentServices();

    // External API.

    // Don't directly change the selected visit requests or services in these functions,
    // they will respond to the focused visit request changing.
    function cancelFocusedEditing() {
        setFocusedVisitRequestState(null);
    }

    async function commitSelectedServiceAssignments(
        options?: Pick<MutationHookOptions, 'onError' | 'onCompleted'>,
    ) {
        const visitRequestRows = selectedVisitRequests.filter(
            (row) => row.__typename === 'VisitRequest',
        );
        const visitRows = selectedVisitRequests.filter((row) => row.__typename === 'Visit');

        const visitRequestAssignments: IUpdateAppointmentServicesInput[] = visitRequestRows.flatMap(
            (row) =>
                getAppointments(row).map((appointment, index) => ({
                    visitRequestId: row.id,
                    clinicianAppointmentId: appointment.id,
                    serviceIds: selectedAppointmentServicesState.at(index)?.serviceIds ?? [],
                })),
        );

        const visitAppointments = visitRows.flatMap((row) =>
            getAppointments(row).map((appointment, index) => ({
                clinicianAppointmentId: appointment.id,
                serviceIds: selectedAppointmentServicesState.at(index)?.serviceIds ?? [],
            })),
        );
        const visitRequestPromises =
            visitRequestAssignments.length > 0
                ? [updateAppointmentServices({ assignments: visitRequestAssignments, options })]
                : [];
        const visitPromises =
            visitAppointments.length > 0
                ? [
                      updateVisitAppointmentServices({
                          input: { clinicianAppointments: visitAppointments },
                          options,
                      }),
                  ]
                : [];

        await Promise.all([...visitRequestPromises, ...visitPromises]);
        cancelFocusedEditing();
    }

    const summaryString = useMemo(() => {
        return renderBulkAppointmentServicesSummaryString(
            selectedAppointmentsToDisplay,
            selectedVisitRequests.length,
        );
    }, [selectedAppointmentsToDisplay, selectedVisitRequests.length]);

    const selectedAppointmentServices: ISelectedAppointmentServices = {
        value: selectedAppointmentServicesState,
        setValue: setSelectedAppointmentServicesState,
        toDisplay: selectedAppointmentsToDisplay,
    };

    const getSelectionState = useCallback(
        (visitRequest: ServicesTableRow): IVisitRequestSelectionState => {
            const { id } = visitRequest;
            const isSelected = selectedVisitRequests.some((visitRequest) => visitRequest.id === id);
            const isCompatibleWithFocusedEditing = isCompatibleWithFocusedSelection(visitRequest);
            const setIsSelected = (selected: boolean) => {
                if (selected) {
                    setSelectedVisitRequests([...selectedVisitRequests, visitRequest]);
                } else {
                    setSelectedVisitRequests(
                        selectedVisitRequests.filter((visitRequest) => visitRequest.id !== id),
                    );
                }
            };
            const focus = () => {
                setFocusedVisitRequestState(visitRequest);
            };
            const cancelFocus = () => {
                setFocusedVisitRequestState(null);
            };
            return {
                isFocused: focusedVisitRequest?.id === visitRequest.id,
                isSelected,
                isCompatibleWithFocusedEditing,
                setIsSelected,
                focus,
                cancelFocus,
            };
        },
        [focusedVisitRequest, selectedVisitRequests, isCompatibleWithFocusedSelection],
    );

    return {
        // Mutation
        mutation: {
            loading:
                isUpdatingVisitRequestAppointmentServices || isUpdatingVisitAppointmentServices,
            commitSelectedServiceAssignments,
            summaryString,
        },

        // Focused Visit Request being Edited
        focusedVisitRequest,
        cancelFocusedEditing,

        // Selected Visit Requests to apply changes to
        selectedVisitRequests,
        setSelectedVisitRequests,
        numSelectedVisitRequests: selectedVisitRequests.length,

        // Util for individual visit requests state info
        getSelectionState,

        // Services Input State
        numSelectedAppointmentServices: selectedAppointmentServices.value.flatMap(
            (appointment) => appointment.serviceIds,
        ).length,
        selectedAppointmentServices,

        // Utilities
        hasFocusedVisitRequest: focusedVisitRequest !== null,
    };
};

const renderBulkAppointmentServicesSummaryString = (
    appointmentServices: { services: { id: string; name: string }[] }[],
    numSelectedVisits: number,
) => {
    const numServices = appointmentServices.flatMap((appointment) => appointment.services).length;
    const servicesStr = numServices > 1 ? 'services' : 'service';
    const numAppointmentsStr = `${appointmentServices.length} appointment${
        appointmentServices.length > 1 ? 's' : ''
    }`;
    const numVisitsStr = `${numSelectedVisits} visit${numSelectedVisits > 1 ? 's' : ''}`;
    if (appointmentServices.length > 1) {
        let str = `Assign ${servicesStr} to ${numAppointmentsStr} in ${numVisitsStr}:`;
        str += appointmentServices
            .map((appointment) => {
                return `\n${appointment.services.map((service) => service.name).join(', ')}`;
            })
            .join('');
        return str;
    }
    return `Assign ${servicesStr} to ${numVisitsStr}: ${(appointmentServices[0]?.services ?? [])
        .map((service) => service.name)
        .join(', ')}`;
};
